/** Restored full historical detail. The production view remains in BomTab.tsx. */
import { useEffect, useState } from "react";
import { bigImg, cleanDesc, cleanPos, f3, hasSheet, n } from "@ic/engine";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { useMoney } from "../../shared/money.ts";
import { api } from "../../shared/api.ts";
import { applyBomTo } from "./ApplyDialog.tsx";
function useApply() {
  const lang = useStore((s) => s.lang);
  return (sc: string) => {
    const { sheet, openModal } = useStore.getState();
    if (hasSheet(sheet)) openModal({ kind: "apply", sc }); // replace / add / cancel (偏差 D6)
    else applyBomTo(sc, "replace", lang);
  };
}

export function FullBomDetail({ sc, onClose, inline }: { sc: string; onClose(): void; inline?: boolean }) {
  const t = useT();
  const m = useMoney();
  const apply = useApply();
  const lang = useStore((s) => s.lang);
  const [d, setD] = useState<Awaited<ReturnType<typeof api.fullBom>> | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set()); // rows picked one by one (JF 2026-10-12)
  useEffect(() => { setSel(new Set()); api.fullBom(sc).then(setD).catch(onClose); }, [sc]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [onClose]);
  if (!d) return null;
  const x = d.item;
  // lines come in cost-sheet order (fabrics, then trims) — the same order and colours as the sheet next to it
  const isFab = (r: (typeof d.lines)[number]) => r[6] === "fabric";
  const fabT = d.lines.reduce((a, r) => a + (isFab(r) ? Number(r[4]) || 0 : 0), 0);
  const trimT = d.lines.reduce((a, r) => a + (isFab(r) ? 0 : Number(r[4]) || 0), 0);
  const toggle = (i: number) => setSel((p) => { const q = new Set(p); if (q.has(i)) q.delete(i); else q.add(i); return q; });
  const allOn = sel.size === d.lines.length && d.lines.length > 0;
  function addPicked() {
    const only = [...sel].sort((a, b) => a - b);
    onClose();
    // an empty sheet gets the style's header as well; otherwise the rows go below what is there
    applyBomTo(sc, hasSheet(useStore.getState().sheet) ? "add" : "replace", lang, only);
  }
  return (
    <DetailWrap inline={inline} onClose={onClose}>
        <button className="ovx" title="Close" onClick={onClose}>×</button>
        <div className="ovtop">
          <div className="ovimg">{x.img ? <img src={bigImg(x.img)} alt="" onError={(e) => { const i = e.currentTarget; if (i.src !== x.img) i.src = x.img; }} /> : <span className="dim">{t("no picture", "无图")}</span>}</div>
          <div>
            <div className="sb1" style={{ color: "var(--accent)", fontWeight: 700, fontSize: 12, letterSpacing: ".06em" }}>{x.cat} · {x.sub}</div>
            <h2 style={{ margin: "4px 0 2px" }}>{x.style || x.sc}</h2><div className="dim">{x.desc}</div>
            <SourceBadge tier={x.tier} />
            <div className="ovsum" style={{ marginTop: 10 }} id="ovCost">
              {/* same order and colours as the cost sheet's cards: fabric, trim, CM, FOB (our cost), customer quote (JF 2026-10-13) */}
              {/* fabric / trim = sums of the BOM lines; material = the figure recorded on the SC itself (JF 2026-10-13) */}
              <div className="tint-fab"><Help text={t("Sum of this BOM's fabric lines: each line unit price × usage, added up — it moves with the usages and prices in the BOM.",
                "BOM 里面料行的合计：每行 单价 × 用量 再相加，会随 BOM 里的用量和单价变化。")} /><span>{t("Fabric", "面料")}</span><b>{m.fmt(fabT)}</b></div>
              <div className="tint-trim"><Help text={t("Sum of this BOM's trim lines: each line unit price × usage, added up — it moves with the usages and prices in the BOM.",
                "BOM 里辅料行的合计：每行 单价 × 用量 再相加，会随 BOM 里的用量和单价变化。")} /><span>{t("Trim", "辅料")}</span><b>{m.fmt(trimT)}</b></div>
              <div className="tint-cm"><span>CM</span><b>{m.fmt(x.cm)}</b></div>
              <div className="ovfob" title={t("Our cost = material + CM (not the customer price)", "我们的成本 = 物料 + CM（不是给客户的价）")}><span>{t("FOB (our cost)", "FOB（成本）")}</span><b>{m.fmt(x.fob)}</b></div>
              <div className="ovqfob" id="ovQuote" title={t("The real per-piece price quoted to the customer (from 查GP)", "给客户的真实每件报价（来自查GP）")}><span>{t("Customer quote $/pc", "客户报价 售价 $/件")}</span>
                <><b>{d.gp?.sell != null ? m.fmt(d.gp.sell) : "—"}</b></></div>
            </div>
            <GpTiles gp={d.gp} qty={x.qty}>
                <div id="ovMat"><Help text={t(`The material cost recorded on this SC itself (not added up from the BOM lines). Compare it with fabric + trim from the BOM (${m.fmt(d.lineTotal)}): a big gap means the BOM may be incomplete, or lists several colourways / whole-order quantities.`,
                  `这个 SC 在系统里直接记录的物料成本（不是由 BOM 各行加出来的）。可以和 BOM 的面料 + 辅料合计（${m.fmt(d.lineTotal)}）对照：差得多说明 BOM 可能不完整，或含多个颜色 / 整单用量。`)} />
                  <span>{t("Material (SC)", "物料（SC）")}</span><b>{m.fmt(x.mat)}</b></div>
            </GpTiles>
            <div className="kv">
              <div>SC</div><div className="mono">{x.sc}</div>
              <div>{t("Customer", "客户")}</div><div>{x.cust}</div>
              <div>{t("Gender", "性别")}</div><div>{x.gender}</div>
              <div>{t("Order qty", "订单量")}</div><div>{x.qty ? Math.round(x.qty) : "—"} pcs</div>
              <div>{t("Factory / country", "工厂 / 国家")}</div><div>{x.fac}{x.co ? " · " + x.co : ""}</div>
              <div>{t("Trade term", "贸易条款")}</div><div><b>{x.tt || "—"}</b></div>
              {/* ex-factory date (JF 2026-10-13): place kept; the developers connect it to the warehouse (IndexItem.exFty) */}
              <div>Ex-fty date</div><div id="ovExFty">{x.exFty || "—"}</div>
              <div>{t("BOM check", "BOM 检查")}</div><div>{x.ok ? <span style={{ color: "#2e6b45" }}>✓ {t("complete", "完整")}</span>
                : <span style={{ color: "#b7791f" }}>⚠ {!x.hasF ? t("no fabric line", "没有面料行") : t("line total ≠ SC material cost", "行合计 ≠ 物料成本")}</span>}</div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn p" data-use-sc={sc} onClick={() => { onClose(); apply(sc); }}>{t("Use this BOM", "使用这个 BOM")}</button>
              {!inline && hasSheet(useStore.getState().sheet) && <button className="btn" id="cmpThis" onClick={() => {
                useStore.getState().setCompare({ on: true, sc }); onClose(); useStore.getState().go("sheet");
              }}>{t("Compare with the current cost sheet", "和当前 cost sheet 对比")}</button>}
              <button className="btn" onClick={onClose}>{t("Back to results", "返回结果")}</button>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 8px", flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: 13, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mut)" }}>BOM · {d.lines.length} {t("lines", "行")}</h3>
          <span className="dim" style={{ fontSize: 12 }}>{t("Tick rows to add only those to the cost sheet", "勾选需要的行，只把这些行加到成本表")}</span>
          <button className="btn sm p" id="addPicked" disabled={!sel.size} onClick={addPicked} style={{ marginLeft: "auto" }}>
            {t(`Add ${sel.size} ticked row(s) to the cost sheet`, `把勾选的 ${sel.size} 行加到成本表`)}</button>
        </div>
        <div style={{ overflowX: "auto" }}><table className="t bomt"><tbody>
          <tr><th><input type="checkbox" id="pickAll" checked={allOn} onChange={() => setSel(allOn ? new Set() : new Set(d.lines.map((_, i) => i)))} title={t("All rows", "全选")} /></th>
            <th>{t("Type", "类型")}</th><th>{t("Description", "描述")}</th><th>{t("Position", "部位")}</th><th>{t("Quality # (supplier article)", "Quality #（供应商货号）")}</th>
            <th>{t("Material code", "物料号")}</th><th className="num">{t("Usage", "用量")}</th><th>{t("Unit", "单位")}</th><th className="num">{t("Unit price", "单价")} {m.sym}</th><th className="num">{t("Amount", "金额")} {m.sym}</th></tr>
          {d.lines.map((r, i) => (
            <tr key={i} className={(isFab(r) ? "tint-fab" : "tint-trim") + (sel.has(i) ? " picked" : "")} data-bomrow={i} onClick={() => toggle(i)} style={{ cursor: "pointer" }}>
              <td><input type="checkbox" checked={sel.has(i)} onChange={() => toggle(i)} onClick={(e) => e.stopPropagation()} /></td>
              <td style={{ whiteSpace: "nowrap" }}>{isFab(r) ? <b>{t("Fabric", "面料")}</b> : String(r[7] || t("Trim", "辅料"))}</td>
              <td>{cleanDesc(r[5]).slice(0, 110)}</td><td>{cleanPos(r[8])}</td><td className="mono">{String(r[11] ?? "")}</td><td className="mono">{String(r[0])}</td>
              <td className="num">{f3(r[1])}</td><td>{String(r[2] ?? "")}</td><td className="num">{f3(n(r[3]) * m.k)}</td><td className="num">{m.num(n(r[4]) * m.k)}</td>
            </tr>
          ))}
          <tr className="tint-cm"><td></td><td style={{ whiteSpace: "nowrap" }}><b>CM</b></td><td colSpan={7} className="dim">{x.fac}{x.qty ? ` · ${Math.round(x.qty)} pcs` : ""}</td><td className="num"><b>{m.num(n(x.cm) * m.k)}</b></td></tr>
        </tbody></table></div>
    </DetailWrap>
  );
}

/**
 * Where the material / CM split of this SC comes from (the v27 demo tool's confidence badge):
 * 1 = computed from the BOM, 2 = real split in the source data, 3 = regression estimate.
 */
const SOURCES: Record<string, [string, string, string]> = {
  "1": ["#2e6b45", "Source: BOM actual · confidence HIGH ●●●", "来源：BOM 实算 · 可信度高 ●●●"],
  "2": ["#1d4ed8", "Source: real split · confidence MID ●●○", "来源：原始真拆分 · 可信度中 ●●○"],
  "3": ["#9a6a00", "Source: regression estimate · confidence LOW ●○○", "来源：回归估算 · 可信度低 ●○○（估算）"],
};
function SourceBadge({ tier }: { tier: string | number }) {
  const t = useT();
  const s = SOURCES[String(tier)];
  if (!s) return null;
  return <div className="srcbadge" id="ovSource" data-tier={tier} style={{ background: s[0] }}
    title={t("How reliable the material and CM figures of this SC are", "这个款的物料、CM 数字有多可靠")}>{t(s[1], s[2])}</div>;
}

/** Real customer quote (selling price), GP % and profit per piece, from 查GP (sc_gp). */
function GpTiles({ gp, qty, children }: { gp: { sell: number | null; gp: number | null; cost: number | null } | null; qty?: number; children?: React.ReactNode }) {
  const t = useT();
  const m = useMoney();
  // the whole order (JF 2026-10-14): revenue = selling price × quantity, profit = (selling price − cost) × quantity
  const q = Math.round(Number(qty) || 0);
  const big = (usd: number) => m.sym + (usd * m.k).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const col = gp?.gp == null ? undefined : gp.gp >= 0 ? "#2e6b45" : "#b42318";
  return (
    <div className="ovsum" id="ovGp">
      {children}
      <div title={t("Real gross-profit % on this order (from 查GP)", "该单真实毛利率（来自查GP）")}><span>GP %</span>
        <><b style={{ color: col }}>{gp?.gp != null ? (gp.gp * 100).toFixed(1) + "%" : t("— (no GP record)", "—（无 GP 记录）")}</b></></div>
      <div title={t("Selling price − cost, per piece", "售价 − 成本，每件")}><span>{t(`Profit ${m.sym}/pc`, `单件毛利 ${m.sym}/件`)}</span>
        <><b style={{ color: col }}>{gp?.sell != null && gp?.cost != null ? m.fmt(gp.sell - gp.cost) : "—"}</b></></div>
      <div id="ovRevenue" title={t(`Selling price × ${q.toLocaleString()} pcs`, `售价 × ${q.toLocaleString()} 件`)}><span>{t("Total revenue", "总收入")}</span>
        <><b>{gp?.sell != null && q ? big(gp.sell * q) : "—"}</b></></div>
      <div id="ovProfit" title={t(`(Selling price − cost) × ${q.toLocaleString()} pcs`, `（售价 − 成本）× ${q.toLocaleString()} 件`)}><span>{t("Total profit", "总利润")}</span>
        <><b style={{ color: col }}>{gp?.sell != null && gp?.cost != null && q ? big((gp.sell - gp.cost) * q) : "—"}</b></></div>
    </div>
  );
}

function DetailWrap({ inline, onClose, children }: { inline?: boolean; onClose(): void; children: React.ReactNode }) {
  return inline
    ? <div className="ovb inline" id="cmpDetail">{children}</div>
    : <div className="ov on" id="ov" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}><div className="ovb" id="ovb">{children}</div></div>;
}

/** A small "?" in the top-right corner of a summary card; the explanation shows on hover (JF 2026-10-13). */
function Help({ text }: { text: string }) {
  return <i className="ovhelp" data-tipx={text}>?</i>;
}
